import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const flat = (color: string, extra: Partial<THREE.MeshStandardMaterialParameters> = {}) =>
  new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0, flatShading: true, ...extra });

const stone = flat("#b9ad98");
const stoneDark = flat("#8f8473");
const walls = flat("#f3c2cf");
const wallsShade = flat("#e6a3b6");
const slate = flat("#3f6fc0");
const gold = flat("#e3b341", { metalness: 0.5, roughness: 0.35 });
const openings = flat("#2b2238");
const greenery = flat("#5f9e4a");

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

function box(width: number, height: number, depth: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const geometry = new THREE.BoxGeometry(width, height, depth);
  geometry.translate(x, y, z);
  return geometry;
}

function cylinder(radius: number, height: number, x: number, y: number, z: number, segments = 12): THREE.BufferGeometry {
  const geometry = new THREE.CylinderGeometry(radius, radius, height, segments);
  geometry.translate(x, y + height / 2, z);
  return geometry;
}

function cone(radius: number, height: number, x: number, y: number, z: number, segments = 12): THREE.BufferGeometry {
  const geometry = new THREE.ConeGeometry(radius, height, segments);
  geometry.translate(x, y + height / 2, z);
  return geometry;
}

/** Toit à deux pans posé sur un bloc `width` × `depth`, faîtage le long de x. */
function gable(width: number, depth: number, rise: number, x: number, y: number, z: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(-depth / 2 - 0.3, 0);
  shape.lineTo(depth / 2 + 0.3, 0);
  shape.lineTo(0, rise);
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: width + 0.4, bevelEnabled: false });
  geometry.translate(0, 0, -(width + 0.4) / 2);
  geometry.rotateY(Math.PI / 2);
  geometry.translate(x, y, z);
  return geometry;
}

interface Tower {
  x: number;
  z: number;
  radius: number;
  height: number;
  spire: number;
}

/**
 * Château de conte de fées, façade principale tournée vers +z : un socle de pierre, un corps de logis
 * rose aux toits d'ardoise bleue, un donjon central coiffé de la plus haute flèche et une couronne de
 * tourelles. Environ 46 m de large pour 50 m de haut, à l'échelle du château d'un parc à thème.
 */
export function buildFairytaleCastle(): THREE.Group {
  const root = new THREE.Group();

  root.add(merged([
    box(46, 3, 32, 0, 1.5, 0),
    box(40, 2, 26, 0, 4, -1),
    box(12, 0.6, 10, 0, 0.3, 19),
  ], stone, "Base"));
  root.add(merged([
    ...[-1, 1].map((side) => box(14, 1, 0.8, side * 15, 3.5, 16.2)),
    ...Array.from({ length: 9 }, (_, i) => box(0.9, 0.7, 0.9, -22 + i * 5.5, 3.35, -16)),
  ], stoneDark, "Battlements"));
  root.add(merged([
    box(10, 0.8, 4, -16, 5.4, 10),
    box(10, 0.8, 4, 16, 5.4, 10),
  ], greenery, "Gardens"));

  const bodies: [number, number, number, number, number][] = [
    [0, -2, 22, 16, 14],
    [-13, 0, 8, 12, 10],
    [13, 0, 8, 12, 10],
    [0, 7, 10, 6, 18],
    [-7, -9, 8, 6, 17],
    [7, -9, 8, 6, 16],
  ];
  const baseY = 5;
  root.add(merged(bodies.map(([x, z, w, d, h]) => box(w, h, d, x, baseY + h / 2, z)), walls, "Walls"));
  root.add(merged(bodies.map(([x, z, w, d, h]) => gable(w, d, Math.min(w, d) * 0.55, x, baseY + h, z)), slate, "Roofs"));

  const towers: Tower[] = [
    { x: 0, z: -2, radius: 4.2, height: 30, spire: 19 },
    { x: -6, z: 6, radius: 2.2, height: 24, spire: 11 },
    { x: 6, z: 6, radius: 2.2, height: 24, spire: 11 },
    { x: -11, z: -6, radius: 2.4, height: 22, spire: 12 },
    { x: 11, z: -6, radius: 2.4, height: 22, spire: 12 },
    { x: -18, z: 5, radius: 2, height: 15, spire: 8 },
    { x: 18, z: 5, radius: 2, height: 15, spire: 8 },
    { x: -18, z: -6, radius: 1.8, height: 13, spire: 7 },
    { x: 18, z: -6, radius: 1.8, height: 13, spire: 7 },
    { x: -3.5, z: 10.5, radius: 1.4, height: 21, spire: 7 },
    { x: 3.5, z: 10.5, radius: 1.4, height: 21, spire: 7 },
    { x: 4, z: -11, radius: 1.6, height: 26, spire: 9 },
    { x: -4, z: -12, radius: 1.3, height: 20, spire: 7 },
  ];
  root.add(merged(towers.map((t) => cylinder(t.radius, t.height, t.x, baseY, t.z)), wallsShade, "Towers"));
  root.add(merged(towers.map((t) => cylinder(t.radius * 1.18, 0.7, t.x, baseY + t.height - 0.7, t.z)), gold, "TowerRings"));
  root.add(merged(towers.map((t) => cone(t.radius * 1.25, t.spire, t.x, baseY + t.height, t.z)), slate, "Spires"));
  root.add(merged(towers.flatMap((t) => [
    new THREE.SphereGeometry(t.radius * 0.18, 8, 6).translate(t.x, baseY + t.height + t.spire, t.z),
    cylinder(t.radius * 0.05 + 0.05, t.radius * 0.9 + 0.6, t.x, baseY + t.height + t.spire, t.z, 5),
  ]), gold, "Finials"));

  const gateArch = new THREE.CylinderGeometry(2.4, 2.4, 1, 16, 1, false, 0, Math.PI);
  gateArch.rotateX(Math.PI / 2);
  gateArch.rotateZ(Math.PI / 2);
  gateArch.translate(0, baseY + 5, 10.05);
  root.add(merged([
    box(4.8, 5, 1, 0, baseY + 2.5, 10.05),
    gateArch,
    ...[-1, 1].flatMap((side) => [0, 1, 2].map((row) => box(1, 1.8, 0.4, side * (2.5 + row * 3.6), baseY + 9 + (row % 2) * 1.5, 4.9 + (row === 0 ? 5.1 : 0)))),
    ...Array.from({ length: 6 }, (_, i) => box(0.8, 1.6, 0.4, -7.5 + i * 3, baseY + 7, 6.05)),
  ], openings, "Openings"));
  root.add(merged([
    box(5.6, 0.5, 1.2, 0, baseY + 7.6, 10.1),
    box(0.5, 0.5, 0.5, 0, baseY + 18.6, 10.1),
  ], gold, "Ornaments"));

  return root;
}
